import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ajouterUnParticipant,
  deposerDesImages,
  ecrireSurUnTableau,
  entrerDansUnCours,
  lireLeTableauCourant,
  mettreAJourSurUnTableau,
  ouvrirUnTableau,
  relireUneScene,
  repondreALaFenetreDInactivite,
  retirerDuTableau,
  sortirDUnCours,
  terminerUnCours,
} from '@groupe-trousseau/shared/api-client';
import { deballer } from '../../shared/api/client';
import { CLES } from '../../shared/donnees/cles';
import {
  identiteDe,
  type ElementDeTableau,
  type PlacementSurLaGrille,
} from './moteur';

/**
 * **Les données du cours et de son tableau — `CDC-2` F-15, F-21, F-23.**
 *
 * **Aucune page n'appelle l'API** : elle consomme ces hooks nommés. C'est aussi ce qui
 * fait qu'aucun `fetch` n'existe hors de l'enrobage du client généré.
 */

/** **F-15 — entrer dans un cours.** Le chemin est permanent tant que le cours vit. */
export function useEntrerDansLeCours(coursId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => deballer(entrerDansUnCours({ path: { id: coursId } })),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: CLES.arrivee });
    },
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-21 — LIRE LE TABLEAU VIVANT, SANS EN CRÉER AUCUN — D-110, seconde
 * correction de contrat.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **C'est ce hook qui ferme le trou de la jauge à zéro.** D-110 §4 : « entre l'arrivée
 * et le premier trait, la jauge d'un tableau presque plein annonce **zéro**, puis se
 * corrige » — parce que le poids ne venait que des réponses d'écriture et de retrait, et
 * qu'« aucune route ne rendait l'état du tableau courant **sans en créer un** ».
 *
 * **Il remplace le salon comme source du tableau vivant, et c'est un gain de fraîcheur.**
 * Le salon porte cette valeur telle qu'elle était **au handshake** ; celle-ci est relue à
 * l'arrivée, et se réinvalide comme n'importe quelle autre lecture. Le salon reste le
 * repli — s'il faut choisir entre deux sources, on préfère celle qui vient d'être lue.
 *
 * **`staleTime` est infini pour la même raison que la scène** : ce qui arrive en direct
 * passe par le canal, et une relecture périodique n'apprendrait rien qu'une écriture ne
 * dise déjà par sa réponse.
 */
export function useTableauCourant(coursId: string, active: boolean) {
  return useQuery({
    queryKey: CLES.cours.tableauCourant(coursId),
    queryFn: () => deballer(lireLeTableauCourant({ path: { id: coursId } })),
    enabled: active,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * **F-21 — rendre le tableau vivant du cours, ou en ouvrir un neuf — D-110, première
 * correction de contrat.**
 *
 * **La route est devenue un « rends-le ou crée-le » le 20260828**, et l'appel ne se lit
 * plus comme avant : elle n'ouvre un tableau neuf que si le cours n'en porte aucun, ou
 * si le sien est au plafond de poids. Sa réponse porte `cree`, qui dit lequel des deux a
 * eu lieu — un tableau neuf est vide, un tableau rendu est garni.
 */
export function useOuvrirUnTableau(coursId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => deballer(ouvrirUnTableau({ path: { id: coursId } })),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: CLES.cours.tout });
    },
  });
}

/**
 * **F-26 — relire une scène par son rang.**
 *
 * Sert deux surfaces et c'est voulu : **le tableau vivant s'y recharge** — « la scène est
 * rechargée entière à chaque retour dans le cours » (F-21) —, et **la relecture d'un cours
 * passé s'y ouvre** depuis l'historique, « le seul chemin pour un participant » (F-47).
 * Deux lectures d'un même objet ne font pas deux routes.
 */
export function useScene(coursId: string, rang: number, active: boolean) {
  return useQuery({
    queryKey: CLES.cours.tableau(coursId, rang),
    queryFn: () =>
      deballer(relireUneScene({ path: { id: coursId, rang } })),
    enabled: active,
    // **La scène ne se relit pas toute seule** : ce qui arrive en direct passe par le
    // canal, pas par un rechargement. Une relecture périodique écraserait ce que le
    // canal vient de peindre.
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
}

/**
 * **F-21 et F-30 — écrire sur le tableau.**
 *
 * **`coupureSecondes` n'est pas facultatif par confort** : c'est ce que la borne de perte
 * de contenu compare. « Un poste qui revient d'une coupure **ne remplace le tableau du
 * serveur que si** le serveur n'a rien reçu de personne d'autre depuis son départ, **ou**
 * si sa coupure a duré **moins de trente secondes**. » Sans cette valeur, le serveur ne
 * peut pas arbitrer, et **la perte retomberait sur ceux qui n'avaient rien à se
 * reprocher** — exactement le renversement que F-30 a voulu.
 */
export function useEcrire(tableauId: string) {
  return useMutation({
    mutationFn: (params: {
      readonly elements: readonly ElementDeTableau[];
      readonly coupureSecondes: number | null;
    }) =>
      deballer(
        ecrireSurUnTableau({
          path: { id: tableauId },
          body: {
            elements: params.elements.map((element) => ({
              genre: 'trace' as const,
              // **L'identité de naissance part avec l'élément**, et c'est elle qui rend
              // le tracé effaçable par n'importe quel présent — y compris par un poste
              // qui ne l'a reçu que par le canal. Le port la donne ; cette couche ne
              // sait pas la lire dans l'élément, et n'a pas à savoir.
              identiteClient: identiteDe(element),
              contenu: element,
            })),
            ...(params.coupureSecondes === null
              ? {}
              : { coupureSecondes: params.coupureSecondes }),
          },
        }),
      ),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-22 — RETIRER UN ÉLÉMENT DU TABLEAU, ET C'EST LE GESTE QUI NE PERSISTAIT
 * RIEN.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Avant le 20260828, la gomme n'appelait aucune route.** Elle filtrait l'état local de
 * `PageCours`, et cet état ne portait que ce que **ce poste** venait de poser. Trois
 * conséquences, lues dans le code puis **mesurées au navigateur** :
 *
 * 1. **Gommer un tracé venu de la scène en place ne faisait rien** — il restait à
 *    l'écran, la liste rendue étant reconstruite depuis `scene.data`. Mesuré : un
 *    trait rechargé pèse **1750 pixels d'encre**, et il en pèse **1750 après le
 *    passage de la gomme**.
 * 2. **Gommer un tracé reçu d'un autre poste ne faisait rien non plus**, pour la même
 *    raison — `canal.etat.recus` n'était pas filtré davantage.
 * 3. **Aucun retrait n'était jamais conservé** : au rechargement, tout revenait.
 *    Mesuré : **1 ligne `Stroke` avant la gomme, 1 après**.
 *
 * Or « **tout le monde efface le tracé de tout le monde** » (Q350) est une règle du
 * produit, et deux des trois endroits où « un élève détruit le travail d'un adulte sans
 * recours » passent par elle. Une gomme qui n'efface que ses propres traits, et
 * seulement à l'écran, **n'est pas cette règle** — c'est une autre, que personne n'a
 * écrite.
 *
 * **Le retrait se fait un élément à la fois, et le contrat n'a pas de route de lot.** Le
 * « tout effacer » de F-22 en appelle donc autant qu'il y a d'éléments. C'est assumé :
 * une route de lot serait un changement de contrat, et **le geste reste rare** — au
 * plafond, on ouvre un tableau neuf plutôt que de vider celui-ci.
 */
export function useRetirerDuTableau(tableauId: string) {
  return useMutation({
    mutationFn: (elementId: string) =>
      deballer(retirerDuTableau({ path: { id: tableauId, elementId } })),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-16 — AJOUTER UN ÉLÈVE À UN COURS COMMENCÉ.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le tuteur du cours, et lui seul** (Q185). Un élève peut le lui demander — c'est
 * F-14, une autre route —, et « **il reste seul à décider** » (Q298).
 *
 * **DEUX GARDES, ET CE SONT DES GARDES ET NON DES DÉCLENCHEURS** (Q194) : l'ajout est
 * refusé si la relation n'existe pas, et **il ne la crée pas au passage** ; il est refusé
 * si le plafond d'élèves de la structure est atteint. Les deux refus portent leur code
 * nommé, `RELATION_REQUISE` et `PLAFOND_ELEVES_ATTEINT`, et l'écran les dit tels quels.
 *
 * **L'accès part par email**, un lien d'auto-connexion propre à la personne (Q185, Q236),
 * et **aucun email ne prévient les autres participants** de son arrivée (Q231). Rien de
 * cela ne se décide ici : c'est le serveur qui l'écrit, et l'écran ne l'annonce pas —
 * D-111 a montré ce que coûte un bandeau qui affirme un envoi que rien ne garantit.
 *
 * **La liste des participants change**, donc l'entrée se relit : c'est elle qui porte la
 * liste que l'écran montre, et le panneau de visio en dérive son test de rôle.
 */
export function useAjouterUnParticipant(coursId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: (eleveId: string) =>
      deballer(
        ajouterUnParticipant({ path: { id: coursId }, body: { eleveId } }),
      ),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: CLES.arrivee });
    },
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-15 — SORTIR D'UN COURS, ET « QUITTER » NE LE FAISAIT PAS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Le bouton « Quitter » naviguait vers l'espace, et rien d'autre.** Mesuré au
 * navigateur le 20260828 : après le clic, `Participant` vaut toujours `PRESENT`, la
 * ligne de relevé est toujours **ouverte**, et **aucun appel ne part**.
 *
 * **Trois conséquences, et la troisième mord tout de suite.**
 *
 * 1. **Le tuteur ne voit pas partir son élève**, là où F-15 pose qu'« un élève quitte un
 *    cours de lui-même, **le tuteur le voit partir**, et il peut revenir juste après ».
 * 2. **La cause écrite au relevé est fausse.** La sortie finit par s'écrire, mais par le
 *    balayage de coupure, donc en `DISCONNECTED` — « sorti sur une coupure » — là où la
 *    personne est **partie d'elle-même**. Le relevé porte la cause de sortie (Q208), et
 *    F-67 en lit les agrégats.
 * 3. **La personne reste enfermée dans le cours qu'elle vient de quitter.** « Une
 *    personne n'est présente que dans un cours à la fois » (Q260) : tant que le serveur
 *    la croit là, **entrer dans un autre cours lui est refusé** en
 *    `DEJA_PRESENT_DANS_UN_COURS`, jusqu'à ce que le balayage la sorte.
 *
 * **Le geste est idempotent côté serveur** — « le geste visait à ce que cette personne
 * ne soit plus dans le cours, et elle n'y est pas » —, donc un second clic ne casse
 * rien, et un échec ne doit pas retenir la personne sur un écran qu'elle quitte.
 */
export function useSortirDuCours(coursId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => deballer(sortirDUnCours({ path: { id: coursId } })),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: CLES.arrivee });
    },
  });
}

/** **F-17 — terminer un cours.** Un des huit motifs, et le seul qui soit un geste. */
export function useTerminerLeCours(coursId: string) {
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => deballer(terminerUnCours({ path: { id: coursId } })),
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: CLES.arrivee });
      await cache.invalidateQueries({ queryKey: CLES.historique.tout });
    },
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-18 — RÉPONDRE À LA FENÊTRE D'INACTIVITÉ, ET CE QUI L'A RENDU POSSIBLE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Ce geste a été ARRÊTÉ le 20260828 plutôt que câblé** — D-118 —, et le motif était
 * structurel : « la route fait quelque chose de réel, mais **rien ne dit à une surface
 * que la fenêtre est ouverte**, et il n'y a donc rien à quoi répondre ». Trois mesures le
 * disaient : aucun événement du canal ne l'annonçait, aucune réponse du contrat ne portait
 * l'instant d'activité, et le serveur le lisait sur une colonne que le front ne voit pas.
 *
 * **Ce qui a changé est l'annonce, pas la route** : le canal porte `activite` (D-120), et
 * le bouton n'apparaît que quand le serveur a dit que la fenêtre est posée. Câbler ce hook
 * avant cette annonce aurait fait verdir le contrôle des appelants sur **un bouton que
 * rien ne fait paraître**.
 *
 * **Ce que la réponse fait, exactement** : elle repousse la fenêtre **pour tout le
 * cours** — donc pour tout le monde, et pas seulement pour qui a cliqué. La déconnexion
 * individuelle du critère 2 de F-18 n'est servie par rien, et l'écart est écrit au cahier
 * des charges et à D-120 plutôt que suggéré ici.
 *
 * **Aucune invalidation de cache** : ce que la réponse change est annoncé par le canal, au
 * tour de balayage suivant, **à tous les présents en même temps**. Rafraîchir localement
 * aurait donné à celui qui clique une fenêtre refermée avant les autres, ce qui est
 * exactement ce que « en même temps » interdit.
 */
export function useRepondreALaFenetre(coursId: string) {
  return useMutation({
    mutationFn: () =>
      deballer(repondreALaFenetreDInactivite({ path: { id: coursId } })),
  });
}


/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-22 — METTRE À JOUR UN ÉLÉMENT DÉJÀ POSÉ, ET C'EST LA REMONTÉE DE D-121
 * SERVIE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * D-121 : « la route d'écriture **crée**, et aucune route ne met à jour. **Déplacer ou
 * redimensionner un élément déjà annoncé ne se conserve pas**, il revient au
 * rechargement là où il est né. La surface écrite à la main avait la même limite et
 * **personne ne pouvait la voir**, faute d'outil de sélection. Le moteur en apporte un,
 * donc **la limite devient visible à l'écran.** »
 *
 * **L'adressage est l'identité de naissance**, celle que le port donne et que le canal
 * transporte — la seule qu'un poste connaisse d'un élément qu'il n'a pas posé lui-même.
 *
 * **Aucune invalidation.** La réponse porte l'état du tableau, comme l'écriture et le
 * retrait, et la jauge le lit de là. Relire la scène écraserait ce que le moteur tient.
 */
export function useMettreAJourSurUnTableau(tableauId: string) {
  return useMutation({
    mutationFn: (params: {
      readonly identite: string;
      readonly element: ElementDeTableau;
    }) =>
      deballer(
        mettreAJourSurUnTableau({
          path: { id: tableauId, identite: params.identite },
          body: { genre: 'trace', contenu: params.element },
        }),
      ),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-24 — DÉPOSER UNE IMAGE OU UN PDF SUR LE TABLEAU.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * **Sept chemins d'entrée, et UNE SEULE ROUTE** (Q356) : bouton d'import, collage,
 * glisser-déposer, photo prise sur le moment, fichier pris dans son espace, adresse web
 * collée, capture d'une partie du tableau. `origine` les nomme tous, et elle n'est pas
 * décorative — « photo » est **le seul** que la caméra occupée refuse (Q415, DD-19).
 *
 * **LE PRODUIT N'ÉMET AUCUN APPEL SORTANT VERS UNE ADRESSE FOURNIE PAR UNE PERSONNE**
 * (F-24 critère 5, Q360). C'est une frontière de sécurité, pas une préférence : le
 * chemin « adresse » envoie ici **des octets que le navigateur de la personne a déjà
 * téléchargés**, exactement comme les six autres. Rien de cette feature ne prend une
 * URL en entrée.
 *
 * **Le placement part avec les octets** (Q362), et il vient du port, seul à connaître la
 * grille. Sans lui, l'image reviendrait au rechargement à une place que personne n'a
 * choisie.
 *
 * **Aucune invalidation de la scène** : la réponse porte l'adresse signée de ce qui vient
 * d'être posé, précisément pour que la surface le peigne **sans relire la scène** — une
 * relecture écrirait une ligne d'audit (DD-12) et repousserait la fenêtre d'inactivité.
 */
export type OrigineDuDepot =
  | 'import'
  | 'collage'
  | 'glisser'
  | 'photo'
  | 'espace'
  | 'adresse'
  | 'capture';

export function useDeposerUneImage(tableauId: string) {
  return useMutation({
    mutationFn: (params: {
      readonly fichier: File;
      readonly origine: OrigineDuDepot;
      readonly type: 'image' | 'pdf';
      readonly placement: PlacementSurLaGrille;
    }) =>
      deballer(
        deposerDesImages({
          path: { id: tableauId },
          // **TOUT PART EN CHAÎNES, ET C'EST CE QUE LE TRANSPORT LIVRE** — D-072 :
          // « un envoi multipart n'a qu'un type de valeur ». Le contrat décrit donc
          // ce qui voyage, et le serveur ramène les quatre nombres par
          // `nombreDeFormulaire`. Les écrire en nombres ici les enverrait de toute
          // façon en chaînes, mais sans que le contrat le dise.
          body: {
            fichier: params.fichier,
            type: params.type,
            origine: params.origine,
            positionX: String(params.placement.positionX),
            positionY: String(params.placement.positionY),
            largeur: String(params.placement.largeur),
            hauteur: String(params.placement.hauteur),
          },
        }),
      ),
  });
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **F-26 ET F-47 — CIRCULER ENTRE LES TABLEAUX D'UN COURS.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « **On circule librement entre les tableaux d'un cours, pendant et après**, les
 * précédents en lecture seule » (Q378). « **La surface montre le rang du tableau courant
 * et le nombre de tableaux du cours**, et on circule de l'un à l'autre » (F-47).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **POURQUOI CHAQUE CIRCULATION RELIT LE SERVEUR, ALORS QUE LA SCÈNE EST EN
 * CACHE POUR TOUJOURS.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * `useScene` porte `staleTime: Number.POSITIVE_INFINITY`, et il le porte pour une
 * bonne raison : « une relecture périodique écraserait ce que le canal vient de
 * peindre ». **Mais une circulation vers un rang déjà visité ne rappellerait alors pas
 * le serveur** — et deux règles en dépendent, aucune des deux n'étant de l'affichage :
 *
 * 1. « **La lecture d'une scène laisse sa ligne de trace, quel que soit le lecteur** »
 *    (DD-12). Une seconde visite servie du cache n'en laisse aucune.
 * 2. « **Quand quelqu'un circule vers un autre tableau du cours, le système doit
 *    repousser la fenêtre pour tout le cours** » (F-18 critère 3). Le serveur le fait
 *    **dans** `relireUneScene`, par `circulerEstUneActivite` — donc un appel qui ne part
 *    pas est une activité qui n'a pas lieu, et la fenêtre s'ouvrirait sur un cours où
 *    quelqu'un travaille.
 *
 * **La circulation invalide donc la scène visée avant d'y aller.** Ce n'est pas un
 * rechargement périodique — c'est un geste, et le geste EST la lecture.
 *
 * **`enRelecture` n'est pas `gele`, et les confondre dirait à quelqu'un qui relit que son
 * tableau est plein.** Voir la prop `lectureStricte` du port.
 */
export function useCirculationEntreTableaux(
  coursId: string,
  rangVivant: number | null,
) {
  const cache = useQueryClient();
  const [rangVisite, poserLeRang] = useState<number | null>(null);

  // **Le rang visité est oublié dès que le tableau vivant le rattrape** : quand un
  // tableau neuf s'ouvre, le rang qu'on regardait devient un rang passé, et rester
  // dessus sans le dire ferait croire qu'on écrit là où on ne peut plus.
  const rang = rangVisite ?? rangVivant;
  const enRelecture = rangVisite !== null && rangVisite !== rangVivant;

  const circuler = useCallback(
    async (vers: number) => {
      await cache.invalidateQueries({
        queryKey: CLES.cours.tableau(coursId, vers),
      });
      poserLeRang(vers === rangVivant ? null : vers);
    },
    [cache, coursId, rangVivant],
  );

  const revenirAuVivant = useCallback(() => {
    poserLeRang(null);
  }, []);

  return { rang, enRelecture, circuler, revenirAuVivant };
}
